<script setup lang="ts">
import { ROOM_TYPES, FLOORS } from '~/composables/useOfferteWizard'

const {
  rooms,
  fieldErrors,
  totalM2,
  recommendedPackage,
  addRoom,
  removeRoom,
  updateRoom,
} = useOfferteWizard()

const adviceText = computed(() => {
  if (rooms.value.length === 0) return 'Voeg een ruimte toe voor een advies op maat.'
  if (totalM2.value <= 0) return 'Vul de oppervlakte in voor een passend advies.'
  if (recommendedPackage.value) {
    return `Op basis van ${rooms.value.length} ruimtes en ${totalM2.value} m² past ${recommendedPackage.value.data.title} het beste bij jouw woning.`
  }
  return `Met ${rooms.value.length} ruimtes en ${totalM2.value} m² adviseren we een maatwerkoplossing — wij rekenen dit voor je uit.`
})

const adviceMatch = computed(() => totalM2.value > 0 && rooms.value.length > 0)
</script>

<template>
  <section class="pa-wizard__panel">
    <div class="pa-wizard__panel-head">
      <h2>Configureer je woning</h2>
      <p>Voeg de ruimtes toe die je wilt koelen of verwarmen. Op basis van het totaal aantal m² geven we je direct een passend advies.</p>
    </div>

    <div class="pa-wizard__rooms">
      <div
          v-for="room in rooms"
          :key="room.id"
          class="pa-wizard__room"
      >
        <div class="pa-wizard__room-fields">
          <div class="pa-wizard__field">
            <label>Type ruimte</label>
            <select
                :value="room.type"
                @change="(e) => updateRoom(room.id, { type: (e.target as HTMLSelectElement).value })"
            >
              <option v-for="t in ROOM_TYPES" :key="t" :value="t">{{ t }}</option>
            </select>
          </div>
          <div class="pa-wizard__field">
            <label>Verdieping</label>
            <select
                :value="room.floor"
                @change="(e) => updateRoom(room.id, { floor: (e.target as HTMLSelectElement).value })"
            >
              <option v-for="f in FLOORS" :key="f" :value="f">{{ f }}</option>
            </select>
          </div>
          <div class="pa-wizard__field">
            <label>Naam (optioneel)</label>
            <input
                type="text"
                placeholder="Bijv. Woonkamer achter"
                :value="room.name"
                @input="(e) => updateRoom(room.id, { name: (e.target as HTMLInputElement).value })"
            >
          </div>
          <div
              class="pa-wizard__field pa-wizard__field--m2"
              :class="{ 'pa-wizard__field--invalid': fieldErrors[`room_${room.id}_m2`] }"
          >
            <label>Oppervlakte</label>
            <input
                type="number"
                min="1"
                max="500"
                inputmode="numeric"
                placeholder="20"
                :value="room.m2 ?? ''"
                @input="(e) => {
                  const v = (e.target as HTMLInputElement).value
                  updateRoom(room.id, { m2: v === '' ? null : Math.max(0, parseInt(v, 10) || 0) })
                }"
            >
            <span class="pa-wizard__room-unit">m²</span>
            <span v-if="fieldErrors[`room_${room.id}_m2`]" class="pa-wizard__error">
              {{ fieldErrors[`room_${room.id}_m2`] }}
            </span>
          </div>
        </div>
        <button
            type="button"
            class="pa-wizard__room-del"
            :aria-label="`Ruimte ${room.type} verwijderen`"
            @click="removeRoom(room.id)"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </button>
      </div>
    </div>

    <button type="button" class="pa-wizard__add-room" @click="addRoom()">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
      Ruimte toevoegen
    </button>

    <p v-if="fieldErrors._rooms" class="pa-wizard__error pa-wizard__error--block">
      {{ fieldErrors._rooms }}
    </p>

    <div class="pa-wizard__config-summary">
      <div class="pa-wizard__cs-stat">
        <div class="pa-wizard__cs-n">{{ rooms.length }}</div>
        <div class="pa-wizard__cs-l">ruimtes</div>
      </div>
      <div class="pa-wizard__cs-stat">
        <div class="pa-wizard__cs-n">{{ totalM2 }} m²</div>
        <div class="pa-wizard__cs-l">totaal vloeroppervlak</div>
      </div>
      <div class="pa-wizard__cs-advice" :class="{ 'pa-wizard__cs-advice--match': adviceMatch }">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 16v-4" />
          <path d="M12 8h.01" />
        </svg>
        <span>{{ adviceText }}</span>
      </div>
    </div>
  </section>
</template>
